/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import RouteNames from '../../appAuthorized/RouteNames';

import { ScrollAwareContext, ScrollAwareScroller, ScrollAwareAppNavStep } from '../../components/ScrollAware';
import { AppHeaderScrollableText } from '../../components/appNav/AppHeaderScrollable';
import ContentWrapper from '../../components/ContentWrapper';

import { HealthEntryWhenCurrent } from './components/HealthEntryWhenCurrent';
import { HealthEntryWhenPast } from './components/HealthEntryWhenPast';

import { generalHealthEntry, generalHealthEntryValue } from '../../../models/HealthEntryCompose';
import DateUtil from '../../../utilities/DateUtil';
import { Card } from '../../../bphComponents/bundle';
import { useHealthEntryForActiveProfile, useHealthEntryAndValueForActiveProfile } from '../../hooks/useHealthEntry';
import { useActiveUser } from '../../hooks/useUser';
import { useNavigate } from '../../hooks/useNavigate';
import { useUIState } from '../../hooks/useUIState';
import HealthEntryCategoryNames from '../../../constants/HealthEntryCategoryNames';
import { useActiveProfile } from '../../hooks/useProfile';
import ProfileModel from '../../../models/ProfileModel';

const getInvalidMessage = (healthEntryData, preferredLanguageCode) => {
  const healthEntry = generalHealthEntry(healthEntryData, preferredLanguageCode);

  const maxEndDate = DateUtil.getMaxEndDate(healthEntry.endDate.data());
  let viewErrorMessage = '';

  if (healthEntry.period.isPeriod()) {
    if (healthEntry.endDate.hasDate() && healthEntry.startDate.hasDate() && DateUtil.isBefore(maxEndDate, healthEntry.startDate.date())) {
      viewErrorMessage = 'error.date-validator.end-before-start.message';
    }
  }

  return viewErrorMessage;
};

interface Props {
  earliestDateLimit: string;
  latestDateLimit: string;
  warningHint: string;
  earliestDateWarningLimit: string;
  match: any;
}

const useStuffValue = (healthEntryId, healthEntryValueId) => {
  const [heValueState, heValueActions] = useHealthEntryAndValueForActiveProfile(healthEntryId, healthEntryValueId);
  const [profileData] = useActiveProfile();
  const [{ preferredLanguageCode }] = useActiveUser();
  const [, navActions] = useNavigate();

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [healthEntryValue, setHealthEntryValue] = React.useState({});

  const profile = new ProfileModel(profileData.data);
  const yearBefore = DateUtil.subtract(profile.getDateOfBirth(), 1, 'year');
  const isGeneticResult = heValueState.category === HealthEntryCategoryNames.geneVariantResult;

  const hevInstance = generalHealthEntryValue(heValueState.category, healthEntryValue, preferredLanguageCode);

  React.useEffect(() => {
    if (!isLoaded) {
      setHealthEntryValue(heValueState.healthEntryValue);
      setIsLoaded(Boolean(heValueState.healthEntryValue.id));
    }
  });

  return [
    {

      earliestDateLimit: yearBefore,
      warningHint: 'healthEntryDates.beforeBirth.warning.text',
      earliestDateWarningLimit: profile.getDateOfBirth(),

      viewErrorMessage: '',
      isCurrent: false,
      isLoaded,
      isPeriod: false,
      allowTogglePeriod: false,
      startDateValue: hevInstance.date.date(),
      startDatePrecision: hevInstance.date.precision(),
      endDateValue: null,
      endDatePrecision: null,
      isSubmitting,
      phrase: {
        title: {
          id: isGeneticResult ? 'pastDates.geneticTestDate.title.text' : 'healthEntryDates.addDates.button.label',
        },
      },
    }, {
      goBack: () => {
        navActions.goBackTo(RouteNames.healthEntryValue(healthEntryId, healthEntryValueId));
      },
      onChangeEndDate: null,
      onChangeStartDate: (dateData) => {
        const newHevInstance = generalHealthEntryValue(heValueState.category, heValueState.healthEntryValue, preferredLanguageCode);
        newHevInstance.date.set(dateData);
        setHealthEntryValue(newHevInstance.data());
      },
      toggleIsPeriod: null,
      onSubmit: () => {
        heValueActions.apiUpdate(healthEntryValue)
        .then(() => {
          navActions.goBackTo(RouteNames.healthEntryValue(healthEntryId, healthEntryValueId));
        });
      },
    },
  ];
};

const useStuff = (healthEntryId) => {
  const [healthEntryState, healthEntryActions] = useHealthEntryForActiveProfile(healthEntryId);

  const [{ preferredLanguageCode }] = useActiveUser();
  const [, navActions] = useNavigate();

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [healthEntryIsLoaded, setHealthEntryIsLoaded] = React.useState(false);
  const [healthEntry, setHealthEntry] = React.useState({});

  const heInstance = generalHealthEntry(healthEntry, preferredLanguageCode);
  const isCurrent = heInstance.current.isCurrent();

  React.useEffect(() => {
    if (!healthEntryIsLoaded) {
      setHealthEntry(healthEntryState.healthEntry);
      setHealthEntryIsLoaded(Boolean(healthEntryState.healthEntry.id));
    }
  });

  return [
    {
      viewErrorMessage: getInvalidMessage(healthEntry, preferredLanguageCode),
      isCurrent,
      isLoaded: healthEntryIsLoaded,
      isPeriod: heInstance.period.isPeriod(),
      allowTogglePeriod: true,
      startDateValue: heInstance.startDate.date(),
      startDatePrecision: heInstance.startDate.precision(),
      endDateValue: heInstance.endDate.date(),
      endDatePrecision: heInstance.endDate.precision(),
      isSubmitting,
      phrase: {
        title: {
          id: isCurrent ? 'currentDates.title.text' : 'healthEntryDates.addDates.button.label',
        },
      },
    },
    {
      goBack: () => {
        navActions.goBackTo(RouteNames.healthEntry(healthEntryId));
      },
      onChangeEndDate: (dateData) => {
        const newHealthEntry = generalHealthEntry(healthEntry, preferredLanguageCode);
        newHealthEntry.endDate.set(dateData);
        setHealthEntry(newHealthEntry.data());
      },
      onChangeStartDate: (dateData) => {
        const newHealthEntry = generalHealthEntry(healthEntry, preferredLanguageCode);
        newHealthEntry.startDate.set(dateData);
        setHealthEntry(newHealthEntry.data());
      },
      toggleIsPeriod: () => {
        const newHealthEntry = generalHealthEntry(healthEntry, preferredLanguageCode);
        newHealthEntry.period.toggle();
        setHealthEntry(newHealthEntry.data());
      },
      onSubmit: () => {
        setIsSubmitting(true);

        const newHealthEntry = generalHealthEntry(healthEntry, preferredLanguageCode);
        if (!newHealthEntry.period.isPeriod()) {
          newHealthEntry.endDate.clear();
        }

        healthEntryActions.apiUpdate(newHealthEntry.data())
          .then(() => {
            setIsSubmitting(false);
            navActions.goBackTo(RouteNames.healthEntry(healthEntryId));
          });
      },
    },
  ];
};

const HealthEntryWhen: React.FC<Props> = (props) => {
  const { earliestDateLimit, latestDateLimit, warningHint, earliestDateWarningLimit } = props;
  const healthEntryId = Number(props.match.params.healthEntryId);
  const healthEntryValueId = Number(props.match.params.valueId);
  const [stuffState, stuffActions] = healthEntryValueId ? useStuffValue(healthEntryId, healthEntryValueId) : useStuff(healthEntryId);

  const [, uiActions] = useUIState();

  const isInvalid = stuffState.viewErrorMessage !== '';
  const isDisabled = isInvalid || stuffState.isSubmitting;

  React.useEffect(() => {
    if (stuffState.viewErrorMessage) {
      uiActions.showNotification(stuffState.viewErrorMessage);
    }
  }, [stuffState.viewErrorMessage]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (isDisabled) { return; }
    stuffActions.onSubmit();
  };

  return (
    <div>
      <ScrollAwareContext>
        <ScrollAwareAppNavStep
          submitDisabled={isDisabled}
          cancel={stuffActions.goBack}
          submit={onSubmit}
          color="white"
        />

        <ScrollAwareScroller>
          <AppHeaderScrollableText {...stuffState.phrase.title} />

          <ContentWrapper>
            <Card.CardShape>
              <Card.CardContent>
                {
                  stuffState.isCurrent ? (
                    <HealthEntryWhenCurrent
                      startDate={stuffState.startDateValue}
                      startDatePrecision={stuffState.startDatePrecision}
                      onChangeStartDate={stuffActions.onChangeStartDate}
                      onFocusStartDate={() => {}}
                      onCancelStartDate={() => {}}
                      earliestDateLimit={earliestDateLimit}
                      latestDateLimit={latestDateLimit}
                      warningHint={warningHint}
                      earliestDateWarningLimit={earliestDateWarningLimit}
                    />
                  ) : (
                    <HealthEntryWhenPast
                      startDate={stuffState.startDateValue}
                      startDatePrecision={stuffState.startDatePrecision}
                      onChangeStartDate={stuffActions.onChangeStartDate}
                      endDate={stuffState.endDateValue}
                      endDatePrecision={stuffState.endDatePrecision}
                      onChangeEndDate={stuffActions.onChangeEndDate}
                      allowTogglePeriod={stuffState.allowTogglePeriod}
                      isPeriod={stuffState.isPeriod}
                      isPeriodChoices={[
                        {
                          label: <FormattedMessage id="toggleMultipleSame.multipleDays.option.label" />,
                          value: true,
                          name: 'period',
                        },
                        {
                          label: <FormattedMessage id="toggleMultipleSame.sameDay.option.label" />,
                          value: false,
                          name: 'period',
                        },
                      ]}
                      toggleIsPeriod={stuffActions.toggleIsPeriod}
                      onFocusStartDate={() => {}}
                      onCancelStartDate={() => {}}
                      onFocusEndDate={() => {}}
                      onCancelEndDate={() => {}}
                      earliestDateLimit={earliestDateLimit}
                      latestDateLimit={latestDateLimit}
                      warningHint={warningHint}
                      earliestDateWarningLimit={earliestDateWarningLimit}
                    />
                  )
                }
              </Card.CardContent>
            </Card.CardShape>
          </ContentWrapper>
        </ScrollAwareScroller>
      </ScrollAwareContext>
    </div>
  );
};

export default HealthEntryWhen;
